/// <reference lib="WebWorker" />

// Require all dayjs locales
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import dayjs from 'dayjs';
import 'dayjs/locale/de.js';
import 'dayjs/locale/en.js';
import 'dayjs/locale/es.js';
import 'dayjs/locale/fr.js';
import 'dayjs/locale/it.js';
import 'dayjs/locale/pt.js';

import { startTransition, useEffect } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { hydrateRoot } from 'react-dom/client';
import { getInitialNamespaces } from 'remix-i18next/client';

import { sentryClientFilter } from './services/sentry/clientFilter';

import { getAppGlobals } from './components/AppGlobals/utils';

import { isCrawler } from './utils/isCrawler';

import i18n from './i18n';

// Save the original fetch function
const originalFetch = window.fetch;

// Override the fetch function
window.fetch = async (input, options = {}) => {
  function isRemixDataRequest(url: URL) {
    return url.searchParams.has('_data');
  }
  // Normalize input to a Request object
  let request: Request;
  if (input instanceof Request) {
    // Merge options into the existing Request
    const headers = new Headers(input.headers);
    if (options.headers) {
      new Headers(options.headers).forEach((value, key) => {
        headers.set(key, value);
      });
    }
    request = new Request(input, { ...options, headers });
  } else {
    // input is string or URL
    request = new Request(input, options);
  }
  const u = new URL(request.url, window.location.href);
  // If the request is a same-origin request, we can add custom headers
  if (u.origin === window.location.origin) {
    const headers = new Headers(request.headers);

    // check if crypto is available (only in secure contexts)
    if ('crypto' in window && crypto.randomUUID) {
      const correlationId = crypto.randomUUID();
      headers.set('x-correlation-id', correlationId);
      Sentry.setTag('x-correlation-id', correlationId);
    }

    if (isRemixDataRequest(u)) {
      // Ensure accept header exists
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }
      const modifiedRequest = new Request(request, { headers });
      const response = await originalFetch(modifiedRequest);
      // Check if the response is an error but did not come from Remix
      // Cloudflare/HUMAN intercepts Remix calls and sometimes returns a 400+
      // status code. Since it should have hit Remix, we need to add
      // the remix response header so client side code can handle it
      if (
        response.status >= 400 &&
        response.headers.get('x-remix-response') === null &&
        response.headers.get('x-remix-error') === null &&
        response.headers.get('x-remix-catch') === null
      ) {
        const headers = new Headers(response.headers);
        headers.set('x-remix-response', 'yes');
        // Create a new response with the modified headers
        const modifiedResponse = new Response(response.body, {
          status: response.status,
          statusText: response.statusText,
          headers: headers,
        });

        return modifiedResponse;
      }
      return response;
    }
    // Call the original fetch with the modified headers
    const modifiedRequest = new Request(request, { headers });
    return originalFetch(modifiedRequest);
  }
  // Call the original fetch
  return originalFetch(input, options);
};

// This function is called when the HUMAN captcha is successfully completed
window._pxFormId = undefined;
window._pxOnCaptchaSuccess = (isValid: boolean) => {
  if (!window._pxFormId) {
    console.log('Captcha success, but no form id found', isValid);
    return;
  }

  try {
    const form = document.getElementById(window._pxFormId) as HTMLFormElement;
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitButton) {
        submitButton.click();
        console.log('Captcha success and the form was submitted', isValid);
      } else {
        console.log(`Captcha success, but form with id ${window._pxFormId} not found`, isValid);
      }
    } else {
      console.log(`Captcha success, but form with id ${window._pxFormId} not found`, isValid);
    }
  } catch (error) {
    console.log('Captcha success, but there was an error submitting the form', error, isValid);
  } finally {
    window._pxFormId = undefined;
  }
};

// add a boolean to the window object to enable logging of sentry events
// to console for debugging purposes
declare global {
  interface Window {
    __BEEHIIV_CONSOLE_LOG_SENTRY_EVENT__: boolean;
    _pxOnCaptchaSuccess: (isValid: boolean) => void;
    _pxFormId: string | undefined;
  }
}

const appGlobals = getAppGlobals();

try {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register(`/entry.worker.js?v=${appGlobals.RELEASE_VERSION}`, {
        type: 'module',
        scope: '/',
      })
      .catch((error) => {
        // if there's an error registering the service worker, mostly due to a 403 error,
        // from Cloudflare, there's not much we can do, so we just log the error
        // and continue without the service worker
        console.error('Service Worker registration failed:', error);
      });
  }
} catch (error) {
  // If the service worker registration fails, we log the error but continue
  // without the service worker. This is important for environments where service workers
  // are not supported or when the registration fails due to network issues.
  console.error('Service Worker registration failed:', error);
}

Sentry.init({
  dsn: appGlobals.SENTRY_CLIENT_DSN,
  environment: appGlobals.SENTRY_ENV,
  release: appGlobals.SENTRY_RELEASE || 'local',
  tracesSampleRate: 0, // disable performance sampling
  replaysSessionSampleRate: 0, // disable session replay
  replaysOnErrorSampleRate: 0,
  sendClientReports: false,
  beforeSend(event, hint) {
    // Don't clog up slack sentry with development errors
    if (process.env.NODE_ENV === 'development' && !appGlobals.ENABLE_SENTRY_DEV) {
      return null;
    }

    // ignore errors from bots/crawlers
    if (isCrawler(navigator.userAgent)) {
      return null;
    }

    // Filter 4xx client errors (bad requests, not found, forbidden, etc.)
    // These are user/bot errors making invalid requests, not actionable server exceptions
    // Just show the error page to the user, don't log to Sentry
    const error = hint.originalException;
    if (error instanceof Response && error.status >= 400 && error.status < 500) {
      return null;
    }

    if (sentryClientFilter(event, hint)) {
      return null;
    }
    // mark event as processed by the beforeSend callback to confirm all events go through the filters
    event.tags = event.tags || {};
    event.tags.processed = 'true';

    return event;
  },

  integrations: [
    Sentry.browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
  ],
});

async function hydrate() {
  const htmlElement = document.documentElement;

  if (process.env.NODE_ENV === 'development') {
    // Check if the style attribute exists and has our stupid --vsc-domain property
    // this was causing false positive hydration issues
    if (htmlElement.style.getPropertyValue('--vsc-domain')) {
      htmlElement.style.removeProperty('--vsc-domain'); // Check if the style is now empty (no properties left)
      if (htmlElement.style.length === 0) {
        htmlElement.removeAttribute('style');
      }
    }
  }
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <RemixBrowser />
      </I18nextProvider>,
      {
        // React 19 error handlers - use Sentry's reactErrorHandler for proper sourcemap support
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          if (process.env.NODE_ENV === 'development') {
            // biome-ignore lint/suspicious/noConsole: Development debugging - captured by Datadog and Sentry breadcrumbs
            console.warn('Uncaught error', error, errorInfo.componentStack);
          }
        }),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      },
    );
  });
}

dayjs.locale(i18next.language);

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}
